import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { monitorUserPool } from '../config/auth';
import { setTokenGetter } from '../config/api';
import api from '../config/api';

interface MonitorUser {
  email: string;
  sub: string;
  role?: 'monitor' | 'lead' | 'admin';
}

interface AuthState {
  user: MonitorUser | null;
  session: CognitoUserSession | null;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getIdToken: () => string | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  });

  const getIdToken = useCallback((): string | null => {
    return state.session?.getIdToken().getJwtToken() ?? null;
  }, [state.session]);

  // Wire up the axios interceptor token getter
  useEffect(() => {
    setTokenGetter(getIdToken);
  }, [getIdToken]);

  // Try to restore session on mount
  useEffect(() => {
    const currentUser = monitorUserPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          setState({ user: null, session: null, loading: false });
          return;
        }
        const idPayload = session.getIdToken().payload;
        setState({
          user: { email: idPayload.email, sub: idPayload.sub },
          session,
          loading: false,
        });
      });
    } else {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  // Probe admin endpoint to determine user role
  useEffect(() => {
    if (!state.user || state.loading || !state.session) return;
    let cancelled = false;
    api.get('/admin/monitors').then((res) => {
      if (cancelled) return;
      const monitors = res.data.monitors as Array<{ email: string; role: string }>;
      const me = monitors.find((m) => m.email === state.user!.email);
      if (me) {
        setState((s) => ({
          ...s,
          user: s.user ? { ...s.user, role: me.role as MonitorUser['role'] } : null,
        }));
      }
    }).catch(() => {
      // 403 or other error — user is not admin, leave role undefined
    });
    return () => { cancelled = true; };
  }, [state.user?.email, state.loading, state.session]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: monitorUserPool,
      });

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idPayload = session.getIdToken().payload;
          setState({
            user: { email: idPayload.email, sub: idPayload.sub },
            session,
            loading: false,
          });
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }, []);

  const logout = useCallback(() => {
    const currentUser = monitorUserPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    setState({ user: null, session: null, loading: false });
  }, []);

  const isAdmin = state.user?.role === 'lead' || state.user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getIdToken, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
